/**
 * Campagnes d'appel — la liste.
 *
 * L'admin crée, lance, met en pause, clôt. Un commercial n'y voit que les
 * campagnes où il est affecté, et ouvre celle qu'il travaille. Une carte dit
 * l'essentiel sans entrer : où en est l'objectif, ce qui reste à appeler, qui
 * est dessus.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  PhoneOutgoing, Plus, Play, Pause, Square, Pencil, Trash2, Loader2, Users, Target, ArrowRight, Package, Clock,
} from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import { PageHeader, Loader, Empty } from "@/crm/ui";
import { useAuth } from "@/context/CrmAuth";
import Modal from "@/crm/components/Modal";
import CallCampaignForm from "@/crm/components/CallCampaignForm";

export const STATUS_META = {
  draft: { label: "Brouillon", cls: "bg-slate-100 text-slate-600" },
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-700" },
  paused: { label: "En pause", cls: "bg-amber-100 text-amber-700" },
  closed: { label: "Clôturée", cls: "bg-slate-200 text-slate-500" },
};

const CARD = "rounded-xl border border-slate-200 bg-white";
const BTN = "inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-50 disabled:opacity-50";

export function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return <span data-testid="cc-status" className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.cls}`}>{m.label}</span>;
}

export function ObjectiveBar({ stats, compact = false }) {
  const o = stats?.objective;
  if (!o || !o.target) return null;
  return (
    <div data-testid="cc-objective">
      <div className="mb-1 flex items-baseline justify-between text-xs text-slate-500">
        <span><b className="font-mono text-slate-800">{o.done}</b> / {o.target} {o.label}</span>
        <span className="font-mono">{o.pct} %</span>
      </div>
      <div className={`overflow-hidden rounded-full bg-slate-100 ${compact ? "h-1.5" : "h-2"}`}>
        <div className="h-full rounded-full bg-indigo-600 transition-[width] duration-500" style={{ width: `${o.pct}%` }} />
      </div>
    </div>
  );
}

export default function CallCampaigns() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => api.get("/call-campaigns").then(({ data }) => setItems(data)).catch(() => setItems([])), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (c, verb, ok) => {
    setBusy(c.id);
    try {
      const { data } = await api.post(`/call-campaigns/${c.id}/${verb}`);
      toast.success(typeof ok === "function" ? ok(data) : ok);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const remove = async (c) => {
    setBusy(c.id);
    try { await api.delete(`/call-campaigns/${c.id}`); toast.success("Campagne supprimée"); setToDelete(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (!items) return <Loader />;

  return (
    <div data-testid="call-campaigns-page">
      <PageHeader title="Campagnes d'appel"
        subtitle={isAdmin
          ? "Une cible figée, une file partagée entre commerciaux, des retentatives automatiques et un objectif."
          : "Les campagnes où vous êtes affecté. Ouvrez-en une pour appeler la prochaine entreprise."}
        actions={isAdmin && (
          <button data-testid="new-call-campaign" onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">
            <Plus size={16} /> Nouvelle campagne
          </button>
        )} />

      {items.length === 0 ? (
        <Empty label={isAdmin ? "Aucune campagne d'appel — créez la première." : "Aucune campagne ne vous est affectée pour l'instant."} icon={PhoneOutgoing} />
      ) : (
        <div className="grid gap-3.5 md:grid-cols-2 xl:grid-cols-3">
          {items.map((c) => {
            const s = c.stats;
            return (
              <div key={c.id} data-testid={`call-campaign-${c.id}`} className={`${CARD} flex flex-col p-5`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-heading text-[16px] font-bold text-slate-900 [text-wrap:pretty]">{c.name}</h3>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><Users size={12} />{c.assignee_names?.length ? c.assignee_names.join(", ") : "Personne d'affecté"}</span>
                      {c.solution_nom && <span className="inline-flex items-center gap-1"><Package size={12} />{c.solution_nom}</span>}
                      {c.hours_label && <span className="inline-flex items-center gap-1"><Clock size={12} />{c.hours_label}{c.status === "active" && !c.open_now ? " · fermée en ce moment" : ""}</span>}
                    </div>
                  </div>
                  <StatusPill status={c.status} />
                </div>

                {c.consigne && <p className="mt-3 line-clamp-2 text-[13px] text-slate-600">{c.consigne}</p>}

                <div className="mt-4">
                  {s ? (
                    <>
                      <ObjectiveBar stats={s} />
                      <div className="mt-3 grid grid-cols-4 gap-1 text-center">
                        {[["À appeler", s.a_appeler + s.en_cours], ["À retenter", s.a_retenter], ["Jointes", s.joints_total], ["RDV", s.rdv]].map(([l, v]) => (
                          <div key={l} className="rounded-lg bg-slate-50 px-1 py-1.5">
                            <div className="font-mono text-sm font-semibold text-slate-800">{v}</div>
                            <div className="text-[10.5px] text-slate-500">{l}</div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                      <Target size={13} /> Pas encore lancée — objectif {c.objective?.target} {c.objective_label}
                    </div>
                  )}
                </div>

                <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-4">
                  <button data-testid={`open-cc-${c.id}`} onClick={() => navigate(`/campagnes/appel/${c.id}`)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-slate-800">
                    Ouvrir <ArrowRight size={14} />
                  </button>
                  {isAdmin && c.status === "draft" && (
                    <button data-testid={`launch-cc-${c.id}`} disabled={busy === c.id}
                      onClick={() => act(c, "launch", (d) => `Lancée : ${d.inserted} entreprise(s) à appeler${d.ecartees_suivies_ailleurs ? ` · ${d.ecartees_suivies_ailleurs} écartée(s), suivies ailleurs` : ""}`)}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-emerald-800 disabled:opacity-50">
                      {busy === c.id ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Lancer
                    </button>
                  )}
                  {isAdmin && c.status === "active" && (
                    <button disabled={busy === c.id} onClick={() => act(c, "pause", "Campagne en pause")} className={BTN}><Pause size={14} /> Pause</button>
                  )}
                  {isAdmin && c.status === "paused" && (
                    <button disabled={busy === c.id} onClick={() => act(c, "resume", "Campagne reprise")} className={BTN}><Play size={14} /> Reprendre</button>
                  )}
                  {isAdmin && (c.status === "active" || c.status === "paused") && (
                    <button disabled={busy === c.id} onClick={() => act(c, "close", "Campagne clôturée — bilan conservé")} className={BTN}><Square size={14} /> Clôturer</button>
                  )}
                  {isAdmin && (
                    <button title="Réglages" aria-label="Réglages" onClick={() => setEditing(c)} className={`${BTN} px-2`}><Pencil size={14} /></button>
                  )}
                  {isAdmin && c.status !== "active" && (
                    <button title="Supprimer" aria-label="Supprimer" onClick={() => setToDelete(c)} className={`${BTN} px-2 text-rose-600 hover:bg-rose-50`}><Trash2 size={14} /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && <CallCampaignForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {editing && <CallCampaignForm campaign={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {toDelete && (
        <Modal onClose={() => setToDelete(null)} size="sm" label="Supprimer la campagne" testid="confirm-delete-cc">
          <h3 className="font-heading text-base font-bold text-slate-900">Supprimer « {toDelete.name} » ?</h3>
          <p className="mt-1 text-[13px] text-slate-500">La file d'appel disparaît. Les appels déjà journalisés restent sur les fiches.</p>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setToDelete(null)} className={BTN}>Annuler</button>
            <button data-testid="confirm-delete-cc-ok" disabled={busy === toDelete.id} onClick={() => remove(toDelete)}
              className="rounded-lg bg-rose-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-rose-700 disabled:opacity-50">Supprimer</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
